import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Stat, PageHead } from "@/components/common";
import { FullLoader } from "@/context/AuthContext";
import { api, errMsg, fmtDT, inr, useApi, BACKEND } from "@/lib/api";

const FIELDS = [["account_name", "Account holder name"], ["account_no", "Account number"], ["ifsc", "IFSC (e.g. SBIN0001234)"], ["bank_name", "Bank name"], ["upi_id", "UPI ID (e.g. name@okicici)"]];

export function PayoutBanner() {
  const { data } = useApi("/payout-details");
  if (!data || data.complete) return null;
  return <p className="mb-6 text-sm text-[#9A5B00] bg-[#FF9700]/10 rounded-xl p-3 flex gap-2" data-testid="payout-missing-banner"><AlertTriangle className="h-4 w-4 shrink-0" />Payout details are mandatory. Add a bank account, UPI ID or UPI QR code in your profile to receive payouts.</p>;
}

export function PayoutDetailsForm() {
  const { data, reload } = useApi("/payout-details");
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (!f) return <FullLoader />;
  const save = async () => {
    const body = Object.fromEntries(FIELDS.map(([k]) => [k, f[k] ? (k === "ifsc" ? f[k].toUpperCase() : f[k]) : null]));
    try { await api.put("/payout-details", body); toast.success("Payout details saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (file) => { const fd = new FormData(); fd.append("file", file); try { await api.post("/payout-details/qr", fd); toast.success("QR uploaded"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="surface p-6 mt-6 max-w-3xl" data-testid="payout-details-form">
      <h3 className="font-semibold flex items-center gap-2">Payout details {data.complete ? <CheckCircle2 className="h-4 w-4 text-[#086B2D]" /> : <span className="text-xs text-[#9A5B00] font-normal">(required – at least one mode)</span>}</h3>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, l]) => <div key={k}><Label className="text-xs muted">{l}</Label><Input className="mt-1" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`payout-${k}`} /></div>)}
        <div><Label className="text-xs muted">UPI QR code</Label>
          <label className="mt-1 flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 text-sm text-[#2412B5]"><Upload className="h-4 w-4" />{data.has_qr ? "Replace QR" : "Upload QR image"}<input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="payout-qr-input" /></label>
          {data.has_qr && <a href={`${BACKEND}/api/payout-details/qr`} target="_blank" rel="noreferrer" className="text-xs text-[#2412B5]">View uploaded QR</a>}</div>
      </div>
      <Button className="mt-4" onClick={save} data-testid="payout-save-btn">Save payout details</Button>
    </section>
  );
}

export function EarningsStatement({ title = "Earnings & payouts", sub }) {
  const { data } = useApi("/my-earnings");
  if (!data) return <FullLoader />;
  return (
    <div data-testid="earnings-statement">
      <PageHead eyebrow="Earnings" title={title} sub={sub} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total sales / fees" value={inr(data.gross_total)} testId="earn-gross" />
        <Stat label="Nirogyam commission" value={inr(data.commission_total)} testId="earn-commission" />
        <Stat label="Net earnings" value={inr(data.net_total)} testId="earn-net" />
        <Stat label="Pending payout" value={inr(data.pending_payout)} hint={`Settled ${inr(data.settled)}`} testId="earn-pending" />
      </div>
      <section className="surface p-6 mt-6 overflow-x-auto">
        <h3 className="font-semibold mb-3">Monthly statement</h3>
        <table className="w-full text-sm"><thead className="text-left text-xs muted"><tr><th className="py-2">Month</th><th>Orders</th><th>Sales</th><th>Commission</th><th>Net</th></tr></thead>
          <tbody>{data.monthly.length === 0 ? <tr><td colSpan={5} className="py-4 muted">No completed sales yet.</td></tr> : data.monthly.map((m) => (
            <tr key={m.month} className="border-t border-[#E2E8ED]" data-testid={`earn-month-${m.month}`}><td className="py-2">{m.month}</td><td>{m.count}</td><td>{inr(m.sales)}</td><td>{inr(m.commission)}</td><td className="font-medium">{inr(m.net)}</td></tr>))}</tbody></table>
      </section>
      {data.payouts.length > 0 && <section className="surface p-6 mt-6"><h3 className="font-semibold mb-3">Payouts received</h3>
        {data.payouts.map((p) => <p key={p.id} className="text-sm py-1">{fmtDT(p.at)} · {inr(p.amount)} · {p.mode.toUpperCase()} · Ref {p.reference}</p>)}</section>}
    </div>
  );
}
